import { useState, useEffect, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, Share2 } from 'lucide-react'
import { API_BASE, apiAssetUrl } from '../config'
import { SafeImage } from '../components/ui/SafeImage'
import { SITE_URL, DEFAULT_OG_IMAGE } from '../data/seo'
import { toEditorCompatibleHtml } from '../utils/richTextLegacy'

interface InsightItem {
  id: number
  title: string
  slug: string
  summary?: string
  cover?: string
  publishedAt?: string
}

type FaqItem = { q?: string; a?: string; question?: string; answer?: string }

type HowToStep = { name?: string; text?: string; title?: string; description?: string }

interface InsightDetail {
  title: string
  slug?: string
  summary?: string
  content: string
  cover?: string
  publishedAt?: string
  tags?: string
  seoTitle?: string
  seoDescription?: string
  seoKeywords?: string
  focusKeyword?: string
  secondaryKeywords?: string
  canonicalPath?: string
  ogTitle?: string
  ogDescription?: string
  ogImage?: string
  schemaType?: string
  authorName?: string
  authorRole?: string
  targetLocations?: string
  primaryCity?: string
  serviceArea?: string
  breadcrumbLabel?: string
  aiSummary?: string
  keyTakeaways?: string[]
  relatedQueries?: string[]
  faq?: FaqItem[]
  howTo?: HowToStep[]
  searchIntent?: string
  reviewedAt?: string
  noindex?: boolean
  nofollow?: boolean
}

function setMetaName(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('name', name)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function setMetaProperty(property: string, content: string) {
  let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('property', property)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function normalizeFaq(items?: FaqItem[]) {
  if (!Array.isArray(items)) return []
  return items
    .map((item) => ({
      q: String(item.q || item.question || '').trim(),
      a: String(item.a || item.answer || '').trim(),
    }))
    .filter((item) => item.q && item.a)
}

export default function InsightDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const [insight, setInsight] = useState<InsightDetail | null>(null)
  const [related, setRelated] = useState<InsightItem[]>([])
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    if (!slug) {
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setNotFound(false)
    fetch(`${API_BASE}/api/public/insights/${slug}`)
      .then((res) => {
        if (res.status === 404) {
          setNotFound(true)
          return null
        }
        return res.json()
      })
      .then((data) => {
        if (cancelled) return
        if (data?.success && data?.insight) {
          setInsight(data.insight)
          return fetch(`${API_BASE}/api/public/insights`)
        }
        setNotFound(true)
      })
      .then((res) => {
        if (cancelled || !res) return
        return res.json()
      })
      .then((data) => {
        if (cancelled || !data?.success || !Array.isArray(data.insights)) return
        const others = data.insights.filter((i: InsightItem) => i.slug !== slug).slice(0, 3)
        setRelated(others)
      })
      .catch(() => setNotFound(true))
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [slug])

  const faqs = useMemo(() => normalizeFaq(insight?.faq), [insight?.faq])
  const takeaways = insight?.keyTakeaways || []
  const howToSteps = useMemo(() => {
    if (!Array.isArray(insight?.howTo)) return []
    return insight.howTo
      .map((s, i) => ({
        name: String(s.name || s.title || `Step ${i + 1}`).trim(),
        text: String(s.text || s.description || '').trim(),
      }))
      .filter((s) => s.name && s.text)
  }, [insight?.howTo])
  const relatedQueries = insight?.relatedQueries || []

  const canonicalPath = useMemo(() => {
    if (!insight) return ''
    const path = insight.canonicalPath || `/insights/${insight.slug || slug}`
    return path.startsWith('/') ? path : `/${path}`
  }, [insight, slug])

  const pageTitle = useMemo(() => {
    if (!insight) return ''
    const raw = insight.seoTitle || `${insight.title} | DigiSutra Insights`
    return raw.length > 70 ? `${raw.slice(0, 67)}…` : raw
  }, [insight])

  const pageDesc = useMemo(() => {
    if (!insight) return ''
    return (
      insight.seoDescription ||
      insight.aiSummary ||
      insight.summary ||
      'Insights on lead generation, Meta Ads, and digital growth. DigiSutra – best digital marketing agency in Surat.'
    ).slice(0, 160)
  }, [insight])

  const ogTitle = useMemo(
    () => (insight?.ogTitle || pageTitle).slice(0, 60),
    [insight?.ogTitle, pageTitle]
  )
  const ogDesc = useMemo(
    () => (insight?.ogDescription || pageDesc).slice(0, 160),
    [insight?.ogDescription, pageDesc]
  )

  // Dynamic SEO / AEO / GEO head tags + JSON-LD (must run before early returns)
  useEffect(() => {
    if (!insight) return

    const canonical = `${SITE_URL}${canonicalPath}`
    const rawImg = insight.ogImage || insight.cover
    const coverResolved = rawImg
      ? rawImg.startsWith('http')
        ? rawImg
        : apiAssetUrl(rawImg)
      : ''
    const ogImage = coverResolved
      ? coverResolved.startsWith('http')
        ? coverResolved
        : `${SITE_URL}${coverResolved}`
      : DEFAULT_OG_IMAGE
    const keywords =
      [
        insight.seoKeywords,
        insight.focusKeyword,
        insight.secondaryKeywords,
        insight.tags,
        insight.primaryCity,
        insight.targetLocations,
      ]
        .filter(Boolean)
        .join(', ') || undefined

    const robotsParts = [
      insight.noindex ? 'noindex' : 'index',
      insight.nofollow ? 'nofollow' : 'follow',
      'max-image-preview:large',
      'max-snippet:-1',
    ]
    document.title = pageTitle
    setMetaName('description', pageDesc)
    if (keywords) setMetaName('keywords', keywords)
    setMetaName('robots', robotsParts.join(', '))
    if (insight.authorName) setMetaName('author', insight.authorName)
    if (insight.focusKeyword) setMetaName('news_keywords', insight.focusKeyword)
    if (insight.aiSummary) setMetaName('abstract', insight.aiSummary.slice(0, 300))

    setMetaProperty('og:title', ogTitle)
    setMetaProperty('og:description', ogDesc)
    setMetaProperty('og:url', canonical)
    setMetaProperty('og:image', ogImage)
    setMetaProperty('og:type', 'article')
    setMetaProperty('og:locale', 'en_IN')
    if (insight.publishedAt)
      setMetaProperty('article:published_time', new Date(insight.publishedAt).toISOString())
    if (insight.reviewedAt)
      setMetaProperty('article:modified_time', new Date(insight.reviewedAt).toISOString())
    if (insight.authorName) setMetaProperty('article:author', insight.authorName)
    if (insight.searchIntent) setMetaProperty('article:section', insight.searchIntent)

    setMetaProperty('twitter:card', 'summary_large_image')
    setMetaProperty('twitter:url', canonical)
    setMetaProperty('twitter:title', ogTitle)
    setMetaProperty('twitter:description', ogDesc)
    setMetaProperty('twitter:image', ogImage)

    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
    if (!canonicalEl) {
      canonicalEl = document.createElement('link')
      canonicalEl.rel = 'canonical'
      document.head.appendChild(canonicalEl)
    }
    canonicalEl.href = canonical

    const schemaType = insight.schemaType || 'Article'
    const locations = [
      insight.primaryCity,
      ...(insight.targetLocations || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      ...(insight.serviceArea || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    ].filter((v, i, a) => v && a.indexOf(v) === i)

    const articleSchema: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': schemaType === 'HowTo' ? 'Article' : schemaType,
      headline: insight.title,
      description: insight.aiSummary || pageDesc,
      image: ogImage,
      datePublished: insight.publishedAt || undefined,
      dateModified: insight.reviewedAt || insight.publishedAt || undefined,
      author: {
        '@type': 'Person',
        name: insight.authorName || 'DigiSutra',
        jobTitle: insight.authorRole || undefined,
      },
      publisher: {
        '@type': 'Organization',
        name: 'DigiSutra',
        logo: {
          '@type': 'ImageObject',
          url: `${SITE_URL}/logo-light.png`,
        },
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': canonical,
      },
      keywords: keywords || undefined,
      about: insight.focusKeyword
        ? { '@type': 'Thing', name: insight.focusKeyword }
        : undefined,
      contentLocation: locations.length
        ? locations.map((name) => ({ '@type': 'Place', name }))
        : undefined,
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['.insight-ai-summary', '.insight-takeaways', 'h1'],
      },
    }

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Knowledge & Insights', item: `${SITE_URL}/insights` },
        {
          '@type': 'ListItem',
          position: 3,
          name: insight.breadcrumbLabel || insight.title,
          item: canonical,
        },
      ],
    }

    const faqSchema =
      faqs.length > 0
        ? {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          }
        : null

    const howToSchema =
      howToSteps.length > 0
        ? {
            '@context': 'https://schema.org',
            '@type': 'HowTo',
            name: insight.title,
            description: insight.aiSummary || pageDesc,
            step: howToSteps.map((s, i) => ({
              '@type': 'HowToStep',
              position: i + 1,
              name: s.name,
              text: s.text,
            })),
          }
        : null

    const scripts: { id: string; data: object }[] = [
      { id: 'insight-article-ld', data: articleSchema },
      { id: 'insight-breadcrumb-ld', data: breadcrumbSchema },
    ]
    if (faqSchema) scripts.push({ id: 'insight-faq-ld', data: faqSchema })
    if (howToSchema) scripts.push({ id: 'insight-howto-ld', data: howToSchema })

    const created: HTMLScriptElement[] = []
    for (const s of scripts) {
      document.getElementById(s.id)?.remove()
      const el = document.createElement('script')
      el.id = s.id
      el.type = 'application/ld+json'
      el.text = JSON.stringify(s.data).replace(/</g, '\\u003c')
      document.head.appendChild(el)
      created.push(el)
    }

    return () => {
      created.forEach((el) => el.remove())
    }
  }, [insight, canonicalPath, pageTitle, pageDesc, ogTitle, ogDesc, faqs, howToSteps])

  const handleShare = () => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      navigator
        .share({
          title: insight?.title,
          url: window.location.href,
          text: insight?.summary,
        })
        .catch(() => {})
    } else {
      navigator.clipboard?.writeText(window.location.href)
    }
  }

  if (loading) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="pt-28 sm:pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="container-narrow">
          <div className="h-6 w-56 bg-slate-200 dark:bg-slate-700 rounded animate-pulse mb-6" />
          <div className="h-[280px] sm:h-[360px] bg-slate-200 dark:bg-slate-700 rounded-2xl animate-pulse mb-8" />
          <div className="space-y-3 max-w-2xl">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-4 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </motion.div>
    )
  }

  if (notFound || !insight) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="pt-28 sm:pt-32 pb-16 px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-slate-600 dark:text-slate-400">Insight not found.</p>
        <Link to="/insights" className="inline-flex items-center gap-2 mt-4 text-primary dark:text-accent font-medium hover:underline">
          <ArrowLeft size={18} /> Back to Knowledge & Insights
        </Link>
      </motion.div>
    )
  }

  const dateStr = insight.publishedAt
    ? new Date(insight.publishedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : ''
  const htmlContent = toEditorCompatibleHtml(insight.content || '')
  const isHtml = Boolean(htmlContent.trim().includes('<'))
  const plainText = htmlContent ? htmlContent.replace(/<[^>]+>/g, ' ') : ''
  const wordCount = plainText.trim() ? plainText.trim().split(/\s+/).length : 0
  const readMinutes = Math.max(1, Math.round(wordCount / 220))

  return (
    <motion.article
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="pt-20 sm:pt-24 pb-16 sm:pb-24"
      itemScope
      itemType="https://schema.org/Article"
    >
      <div className="relative">
        <div className="absolute inset-0 -z-10">
          <SafeImage
            src={insight.cover}
            alt=""
            label={insight.title}
            tone="hero"
            className="h-[380px] w-full object-cover sm:h-[440px]"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/25 to-transparent" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.22),transparent_55%)]" />
        </div>

        <div className="container-wide px-4 sm:px-6 lg:px-8 pt-14 sm:pt-16 pb-10 sm:pb-14">
          <nav className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm text-white/80" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">
              Home
            </Link>
            <span aria-hidden className="opacity-70">
              /
            </span>
            <Link to="/insights" className="hover:text-white transition-colors">
              Knowledge & Insights
            </Link>
            <span aria-hidden className="opacity-70">
              /
            </span>
            <span className="text-white truncate max-w-[240px] sm:max-w-md" title={insight.title}>
              {insight.title}
            </span>
          </nav>

          <div className="mt-6 sm:mt-8 max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.08] text-white" itemProp="headline">
              {insight.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-3 text-white/85 text-sm sm:text-base">
              {insight.authorName && (
                <>
                  <span itemProp="author">
                    {insight.authorName}
                    {insight.authorRole ? ` · ${insight.authorRole}` : ''}
                  </span>
                  <span className="opacity-60">•</span>
                </>
              )}
              {dateStr && (
                <>
                  <time dateTime={insight.publishedAt} itemProp="datePublished">
                    {dateStr}
                  </time>
                  <span className="opacity-60">•</span>
                </>
              )}
              <span>{readMinutes} min read</span>
              {(insight.primaryCity || insight.targetLocations) && (
                <>
                  <span className="opacity-60">•</span>
                  <span>
                    {insight.primaryCity || insight.targetLocations?.split(',')[0]?.trim()}
                  </span>
                </>
              )}
            </div>
            {insight.summary && (
              <p className="mt-5 text-base sm:text-lg text-white/85 leading-relaxed max-w-2xl" itemProp="description">
                {insight.summary}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="container-wide px-4 sm:px-6 lg:px-8 -mt-10 sm:-mt-12">
        <div className="grid lg:grid-cols-[320px_1fr] gap-6 lg:gap-10 items-start">
          <aside className="lg:sticky lg:top-28">
            <div className="card-glass rounded-2xl p-4 sm:p-5 shadow-card">
              <div className="flex items-center justify-between gap-3">
                <Link
                  to="/insights"
                  className="inline-flex items-center gap-2 text-slate-700 dark:text-slate-200 hover:text-primary dark:hover:text-accent font-semibold transition-colors"
                >
                  <ArrowLeft size={18} /> All insights
                </Link>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/70 dark:bg-white/10 hover:bg-white dark:hover:bg-white/15 border border-slate-200/70 dark:border-slate-700 text-slate-800 dark:text-slate-100 transition-colors"
                >
                  <Share2 size={16} /> <span className="text-sm font-semibold">Share</span>
                </button>
              </div>

              <div className="mt-4 pt-4 border-t border-slate-200/70 dark:border-slate-700">
                <div className="text-xs text-slate-500 dark:text-slate-400">Reading time</div>
                <div className="mt-1 text-slate-800 dark:text-slate-100 font-semibold">{readMinutes} minutes</div>
                {dateStr && (
                  <>
                    <div className="mt-3 text-xs text-slate-500 dark:text-slate-400">Published</div>
                    <div className="mt-1 text-slate-800 dark:text-slate-100 font-semibold">{dateStr}</div>
                  </>
                )}
                {insight.focusKeyword && (
                  <>
                    <div className="mt-3 text-xs text-slate-500 dark:text-slate-400">Topic</div>
                    <div className="mt-1 text-slate-800 dark:text-slate-100 font-semibold">{insight.focusKeyword}</div>
                  </>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-slate-200/70 dark:border-slate-700">
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  Want this system for your business? Book a strategy call and we’ll map your next 30 days.
                </p>
                <Link to="/contact" className="btn-primary mt-4 w-full justify-center">
                  Book Strategy Call
                </Link>
              </div>
            </div>
          </aside>

          <div className="card-glass rounded-2xl shadow-card overflow-hidden">
            <div className="p-5 sm:p-8 lg:p-10">
              {insight.aiSummary && (
                <div className="insight-ai-summary mb-8 rounded-2xl border border-primary/20 bg-primary/[0.06] p-5">
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-primary mb-2">
                    Quick answer
                  </p>
                  <p className="text-base sm:text-lg text-slate-800 dark:text-slate-100 leading-relaxed">
                    {insight.aiSummary}
                  </p>
                </div>
              )}

              {takeaways.length > 0 && (
                <div className="insight-takeaways mb-8 rounded-2xl border border-accent/40 bg-accent/10 p-5">
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-800 dark:text-slate-100 mb-3">
                    Key takeaways
                  </p>
                  <ul className="space-y-2">
                    {takeaways.map((t) => (
                      <li key={t} className="flex gap-2 text-sm sm:text-base text-slate-800 dark:text-slate-100">
                        <span className="text-primary font-bold shrink-0">•</span>
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {howToSteps.length > 0 && (
                <div className="mb-8 rounded-2xl border border-slate-200/70 dark:border-slate-700 p-5">
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-800 dark:text-slate-100 mb-3">
                    Step-by-step
                  </p>
                  <ol className="space-y-3 list-decimal pl-5">
                    {howToSteps.map((s) => (
                      <li key={s.name} className="text-slate-800 dark:text-slate-100">
                        <span className="font-semibold">{s.name}</span>
                        <p className="mt-1 text-sm sm:text-base text-slate-600 dark:text-slate-300">
                          {s.text}
                        </p>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              <div
                className="prose prose-slate dark:prose-invert prose-lg max-w-none prose-headings:font-bold prose-headings:text-slate-900 dark:prose-headings:text-white prose-p:text-slate-700 dark:prose-p:text-slate-300 prose-a:text-primary dark:prose-a:text-accent prose-a:no-underline hover:prose-a:underline prose-img:max-w-none prose-img:rounded-xl prose-img:h-auto prose-table:block prose-table:w-full prose-table:overflow-x-auto"
                itemProp="articleBody"
              >
                {isHtml ? (
                  <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
                ) : (
                  <div className="whitespace-pre-wrap text-slate-700 dark:text-slate-300 leading-relaxed">
                    {insight.content || 'No content yet.'}
                  </div>
                )}
              </div>

              {faqs.length > 0 && (
                <section className="mt-10 pt-8 border-t border-slate-200/70 dark:border-slate-700" aria-labelledby="insight-faq-heading">
                  <h2 id="insight-faq-heading" className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white mb-4">
                    Frequently asked questions
                  </h2>
                  <div className="space-y-3">
                    {faqs.map((f, i) => (
                      <details
                        key={i}
                        className="group rounded-2xl border border-slate-200/70 dark:border-slate-700 bg-white/50 dark:bg-white/5 open:bg-white/80 dark:open:bg-white/10"
                      >
                        <summary className="cursor-pointer list-none px-4 py-3.5 font-semibold text-slate-900 dark:text-white flex items-center justify-between gap-3">
                          <span>{f.q}</span>
                          <span className="text-slate-400 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                        </summary>
                        <div className="px-4 pb-4 text-slate-600 dark:text-slate-300 text-sm sm:text-base leading-relaxed whitespace-pre-wrap">
                          {f.a}
                        </div>
                      </details>
                    ))}
                  </div>
                </section>
              )}

              {relatedQueries.length > 0 && (
                <section className="mt-8 pt-6 border-t border-slate-200/70 dark:border-slate-700">
                  <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-3">
                    Related searches
                  </h2>
                  <ul className="flex flex-wrap gap-2">
                    {relatedQueries.map((q) => (
                      <li
                        key={q}
                        className="rounded-full border border-slate-200/80 dark:border-slate-700 bg-white/60 dark:bg-white/5 px-3 py-1.5 text-sm text-slate-700 dark:text-slate-300"
                      >
                        {q}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <div className="mt-10 pt-8 border-t border-slate-200/70 dark:border-slate-700 flex flex-wrap items-center gap-3 justify-between">
                <Link
                  to="/insights"
                  className="inline-flex items-center gap-2 text-primary dark:text-accent font-semibold hover:underline"
                >
                  <ArrowLeft size={18} /> Back to Knowledge & Insights
                </Link>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-accent font-semibold transition-colors"
                >
                  <Share2 size={18} /> Share this insight
                </button>
              </div>
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <section className="mt-14 sm:mt-16">
            <div className="flex items-end justify-between gap-4 mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">More insights</h2>
              <Link to="/insights" className="text-sm font-semibold text-primary dark:text-accent hover:underline">
                View all
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/insights/${item.slug}`}
                  className="group block rounded-2xl overflow-hidden border border-slate-200/70 dark:border-slate-700 bg-white/60 dark:bg-white/5 hover:bg-white/80 dark:hover:bg-white/10 transition-colors shadow-card"
                >
                  <div className="aspect-video overflow-hidden bg-slate-100 dark:bg-slate-800">
                    <SafeImage
                      src={item.cover}
                      alt=""
                      label={item.title}
                      tone="document"
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-slate-900 dark:text-white group-hover:text-primary dark:group-hover:text-accent transition-colors line-clamp-2">
                      {item.title}
                    </h3>
                    {item.summary && (
                      <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 line-clamp-2">{item.summary}</p>
                    )}
                    <div className="mt-4 text-sm font-semibold text-slate-700 dark:text-slate-300 group-hover:text-primary dark:group-hover:text-accent transition-colors">
                      Read →
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </motion.article>
  )
}
